import React, { memo } from 'react';
import { ExternalLink, Play, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import SEOOptimizedImage from './SEOOptimizedImage';

const ChannelCard = memo(({ channel, isDetailed = false }) => {
  const {
    name,
    category,
    accentColorHex,
    subscribers,
    description,
    youtubeUrl,
    logo,
    banner,
    latestVideo,
    content
  } = channel;

  return (
    <motion.div 
      whileHover={{ 
        scale: 1.02,
        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)"
      }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={`channel-card group relative overflow-hidden ${
        isDetailed ? 'col-span-1 lg:col-span-2' : ''
      }`}
      style={{
        '--accent-color': accentColorHex,
      }}
    >
      {/* Glowing border effect */}
      <div 
        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"
        style={{ background: `linear-gradient(to right, ${accentColorHex}, ${accentColorHex})` }}
      ></div>
      
      {/* Background image for detailed view */}
      {isDetailed && (
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(${banner})` }}
        ></div>
      )}
      
      <div className="relative z-10">
        {/* Header */}
        <div className="text-center mb-4">
          <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-800 flex items-center justify-center shadow-lg mx-auto mb-3">
            <SEOOptimizedImage 
              src={logo} 
              alt={`${name} - ${category} channel logo for entertainment and lifestyle content`}
              className="w-full h-full object-cover"
              width={48}
              height={48}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="w-full h-full bg-gray-700 flex items-center justify-center text-white text-sm font-bold" style={{display: 'none'}}>
              {name.split(' ').map(word => word[0]).join('')}
            </div>
          </div>
          <h3 className="text-lg font-bold text-white mb-2">{name}</h3>
          <div className="flex items-center justify-center space-x-2 mb-3">
            <span 
              className="inline-block px-2 py-1 text-xs font-medium rounded-full text-white"
              style={{ backgroundColor: accentColorHex }}
            >
              {category}
            </span>
            <span className="text-xs text-gray-400">{subscribers} subscribers</span>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-300 text-sm mb-4 leading-relaxed line-clamp-3">
          {description}
        </p>

        {/* Content list for detailed view */}
        {isDetailed && content && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-white mb-2">Content Includes:</h4>
            <ul className="space-y-1">
              {content.map((item, index) => (
                <li key={index} className="text-sm text-gray-400 flex items-center">
                  <span 
                    className="w-1 h-1 rounded-full mr-2"
                    style={{ backgroundColor: accentColorHex }}
                  ></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Latest Video Preview for regular cards */}
        {!isDetailed && latestVideo && (
          <div className="mb-4">
            <div className="bg-gray-800 rounded-lg p-2">
              <h5 className="text-xs text-gray-300 mb-1 line-clamp-1">
                {latestVideo.title}
              </h5>
              <div className="flex items-center text-xs text-gray-400">
                <Calendar size={10} className="mr-1" />
                {new Date(latestVideo.publishedAt).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric' 
                })}
              </div>
            </div>
          </div>
        )}

        {/* Latest Video for detailed view */}
        {isDetailed && latestVideo && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-white mb-2 flex items-center">
              <Play size={14} className="mr-1" />
              Latest Video
            </h4>
            <div className="bg-gray-800 rounded-lg p-3">
              <h5 className="text-xs text-gray-300 mb-1 line-clamp-2">
                {latestVideo.title}
              </h5>
              <div className="flex items-center text-xs text-gray-400 mb-2">
                <Calendar size={12} className="mr-1" />
                {new Date(latestVideo.publishedAt).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric' 
                })}
              </div>
              <div className="relative aspect-video bg-gray-700 rounded overflow-hidden">
                <iframe
                  src={`https://www.youtube.com/embed/${latestVideo.videoId}`}
                  title={`Latest ${category} video from ${name} - ${latestVideo.title}`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
            </div>
          </div>
        )}

        {/* Subscribe Button */}
        <div className="text-center">
          <motion.a
            href={youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center space-x-2 px-4 py-2 text-white font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-300 text-sm"
            style={{ backgroundColor: accentColorHex }}
          >
            <span>Subscribe</span>
            <motion.div
              whileHover={{ x: 2 }}
              transition={{ duration: 0.2 }}
            >
              <ExternalLink size={14} />
            </motion.div>
          </motion.a>
        </div>
      </div>
    </motion.div>
  );
});

ChannelCard.displayName = 'ChannelCard';
export default ChannelCard;
